import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  addKlachtAction,
  fetchInvoiceLinesForAdjust,
  KlachtError,
  type InvoiceLineForPicker,
} from '../../../lib/klachten';
import { formatCurrency } from '../../../lib/format';
import { useKlachtDialogStore } from '../../../stores/klacht-dialog-store';

interface Props {
  onAdded?: () => void;
}

type Modus = 'aantal' | 'kortingBedrag' | 'kortingPercentage';

/**
 * Action-dialog: factuur-correctie. Office kiest één regel uit de factuur
 * en één van drie correctie-modi. Apex `InvoiceAdjustActionHandler`
 * herrekent het totaal en zet eventuele Mollie-wijzigingen door.
 */
export function AddInvoiceAdjustDialog({ onAdded }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'addInvoice';
  const klachtId = payload.klachtId;
  const werkOrderId = payload.werkOrderId;

  const [lines, setLines] = useState<InvoiceLineForPicker[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [modus, setModus] = useState<Modus>('kortingBedrag');
  const [waarde, setWaarde] = useState('');
  const [toelichting, setToelichting] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedLineId('');
    setModus('kortingBedrag');
    setWaarde('');
    setToelichting('');
    setSubmitting(false);
    setError(null);
    if (!werkOrderId) {
      setLoadError('Geen werkorder-id meegegeven');
      setLines([]);
      return;
    }
    let cancelled = false;
    setLoadingLines(true);
    setLoadError(null);
    fetchInvoiceLinesForAdjust(werkOrderId)
      .then((res) => {
        if (cancelled) return;
        setLines(res.lines);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof KlachtError ? err.message : 'Factuur-regels laden mislukt';
        setLoadError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoadingLines(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, werkOrderId]);

  const waardeNum = Number(waarde.replace(',', '.'));
  const waardeValid = Number.isFinite(waardeNum) && waardeNum >= 0 && (modus === 'aantal' || waardeNum > 0);

  const handleSubmit = useCallback(async () => {
    if (!klachtId) {
      setError('Geen klacht-id meegegeven');
      return;
    }
    if (!selectedLineId) {
      setError('Selecteer een factuurregel');
      return;
    }
    if (!waardeValid) {
      setError('Vul een geldige waarde in');
      return;
    }
    if (!toelichting.trim()) {
      setError('Toelichting is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        invoiceLineItemId: selectedLineId,
        toelichting: toelichting.trim(),
      };
      if (modus === 'aantal') params.newAantal = waardeNum;
      if (modus === 'kortingBedrag') params.kortingBedrag = waardeNum;
      if (modus === 'kortingPercentage') params.kortingPercentage = waardeNum;

      await addKlachtAction({ klachtId, actionType: 'InvoiceAdjust', params });
      close();
      onAdded?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Factuur aanpassen mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [klachtId, selectedLineId, modus, waardeNum, waardeValid, toelichting, close, onAdded]);

  const noInvoice = loadError && loadError.toLowerCase().includes('factuur');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Factuur aanpassen</h2>
          <p className="text-xs text-slate-500 mt-1">
            Pas één factuurregel aan: aantal, korting in € of korting in %.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          {loadingLines && <p className="text-xs text-slate-500">Factuur-regels laden…</p>}
          {noInvoice && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Geen factuur gekoppeld aan deze werkorder. Kies een andere actie (bv. refund).
            </div>
          )}
          {loadError && !noInvoice && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {loadError}
            </p>
          )}

          {!loadingLines && !loadError && (
            <fieldset>
              <legend className="text-sm text-slate-700 mb-1">
                Factuurregel <span className="text-red-600">*</span>
              </legend>
              <div className="space-y-1">
                {lines.map((line) => (
                  <label
                    key={line.id}
                    className="flex items-start gap-2 px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="invoice-line"
                      value={line.id}
                      checked={selectedLineId === line.id}
                      onChange={() => setSelectedLineId(line.id)}
                      disabled={submitting}
                      className="mt-0.5 border-slate-300"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{line.productName}</div>
                      <div className="text-xs text-slate-500">
                        Aantal {line.aantal} × {formatCurrency(line.prijsInclBtw)} ={' '}
                        {formatCurrency(line.totaalInclBtw)}
                        {line.korting > 0 && ` (korting ${formatCurrency(line.korting)})`}
                      </div>
                    </div>
                  </label>
                ))}
                {lines.length === 0 && (
                  <p className="text-xs text-slate-500">Geen regels gevonden.</p>
                )}
              </div>
            </fieldset>
          )}

          <fieldset className="grid grid-cols-3 gap-2">
            {([
              { code: 'aantal', label: 'Aantal' },
              { code: 'kortingBedrag', label: 'Korting €' },
              { code: 'kortingPercentage', label: 'Korting %' },
            ] as const).map((opt) => (
              <label
                key={opt.code}
                className={clsx(
                  'flex items-center justify-center gap-1 px-2 py-1.5 rounded border text-sm cursor-pointer',
                  modus === opt.code
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                )}
              >
                <input
                  type="radio"
                  name="modus"
                  value={opt.code}
                  checked={modus === opt.code}
                  onChange={() => setModus(opt.code)}
                  disabled={submitting}
                  className="sr-only"
                />
                {opt.label}
              </label>
            ))}
          </fieldset>

          <label className="block">
            <span className="text-sm text-slate-700">
              Waarde <span className="text-red-600">*</span>
            </span>
            <div className="mt-1 flex items-center border border-slate-300 rounded">
              {modus !== 'aantal' && modus !== 'kortingPercentage' && (
                <span className="px-2 text-sm text-slate-500">€</span>
              )}
              <input
                type="text"
                inputMode="decimal"
                value={waarde}
                onChange={(e) => setWaarde(e.target.value)}
                placeholder={modus === 'aantal' ? '0' : '0,00'}
                className="flex-1 px-2 py-1.5 text-sm outline-none"
                disabled={submitting}
              />
              {modus === 'kortingPercentage' && (
                <span className="px-2 text-sm text-slate-500">%</span>
              )}
            </div>
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">
              Toelichting <span className="text-red-600">*</span>
            </span>
            <textarea
              value={toelichting}
              onChange={(e) => setToelichting(e.target.value)}
              rows={3}
              placeholder="Bv. compensatie reinigingsschade — 50% korting"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !selectedLineId || !waardeValid || !toelichting.trim()}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !selectedLineId || !waardeValid || !toelichting.trim()
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {submitting ? 'Bezig…' : 'Factuur aanpassen'}
          </button>
        </div>
      </div>
    </div>
  );
}
